import { useCallback, useState } from "react";
import { Overlay } from "./Overlay";

interface ContextMenuItem {
  readonly label?: string;
  readonly onClick?: () => void;
  readonly danger?: boolean;
  readonly separator?: boolean;
  readonly header?: boolean;
  readonly indent?: boolean;
}

interface ContextMenuProps {
  readonly items: readonly ContextMenuItem[];
  readonly anchorRect: DOMRect;
  readonly onClose: () => void;
}

const VIEWPORT_MARGIN = 8;

function computeMenuPosition(el: HTMLElement, anchorRect: DOMRect): React.CSSProperties {
  const menuW = el.offsetWidth;
  const menuH = el.offsetHeight;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let x = anchorRect.left;
  let y = anchorRect.bottom + 4;

  // Flip above if no room below
  if (y + menuH + VIEWPORT_MARGIN > vh) {
    y = anchorRect.top - menuH - 4;
  }

  // Clamp horizontal
  if (x + menuW + VIEWPORT_MARGIN > vw) {
    x = vw - menuW - VIEWPORT_MARGIN;
  }
  x = Math.max(VIEWPORT_MARGIN, x);
  y = Math.max(VIEWPORT_MARGIN, y);

  return { position: "fixed", left: x, top: y, visibility: "visible" };
}

function useAnchoredPosition(anchorRect: DOMRect) {
  const [style, setStyle] = useState<React.CSSProperties>({
    position: "fixed",
    visibility: "hidden",
  });

  const menuRef = useCallback(
    (el: HTMLDivElement | null) => {
      if (!el) return;
      setStyle(computeMenuPosition(el, anchorRect));
    },
    [anchorRect],
  );

  return { menuRef, style };
}

export function ContextMenu({ items, anchorRect, onClose }: ContextMenuProps) {
  const { menuRef, style } = useAnchoredPosition(anchorRect);

  return (
    <Overlay open backdrop={false} onClose={onClose} center={false}>
      <div
        ref={menuRef}
        className="bg-surface border border-border rounded-xl shadow-xl py-1 min-w-48 animate-scale-in pointer-events-auto z-[9999]"
        style={style}
      >
        {items.map((item, idx) => (
          <div key={idx}>
            {item.separator && (
              <div className="my-1 border-t border-border/30" />
            )}
            {!item.label ? null : item.header ? (
              <div
                className={`w-full text-left px-3 py-1.5 text-[10px] text-ink-faint select-none ${item.indent ? "pl-8" : ""}`}
              >
                {item.label}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  item.onClick?.();
                  onClose();
                }}
                className={`w-full text-left py-2 text-xs font-medium
                  transition-colors focus:outline-none
                  ${item.indent ? "pl-8 pr-3" : "px-3"}
                  ${
                    item.danger
                      ? "text-danger hover:bg-danger/10 active:bg-danger/20"
                      : "text-ink hover:bg-surface-alt active:bg-surface-alt/80"
                  }`}
              >
                {item.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </Overlay>
  );
}
